<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import {
  TSAssetInfo,
  TSBadge,
  TSDialog,
  TSIcon,
  TSImage,
  TSTabMenu,
  TSTimeline,
  formatDate,
  formatUserName,
  getImageURL,
  useLoadingStore,
  useToast,
} from 'tsv2-library';

import CopyButton from '../CopyButton.vue';

import BorrowingServices from '@/services/borrowing.service';
import {
  BorrowingBorrowedByAsset,
  BorrowingTransactionLog,
  BorrowingTransactionRequest,
} from '@/types/borrowing.type';

const visible = defineModel<boolean>('visible', { required: true });

const toast = useToast();
const { setLoading } = useLoadingStore();

const props = defineProps<{
  selectedDatum?: Partial<
    BorrowingTransactionRequest | BorrowingBorrowedByAsset
  >;
  data:
    | Partial<BorrowingTransactionRequest>[]
    | Partial<BorrowingBorrowedByAsset>[];
}>();

const activeTabIndex = ref<number>(0);
const currentIndex = ref<number>(0);
const timelineKey = ref<number>(0);
const transactionLog = ref<BorrowingTransactionLog>();

const statusSeverity = computed(() => {
  switch (props.data[currentIndex.value].status) {
    case 'Waiting for Approval':
    case 'Waiting for Handover':
    case 'Extend Approval':
    case 'Request Extension':
      return 'warning';
    case 'Rejected':
    case 'Reported Damaged':
    case 'Reported Missing':
    case 'TAG Reported':
      return 'danger';
    case 'Returned':
    case 'Approved':
      return 'success';
    default:
      return 'primary';
  }
});

/**
 * Get the currently active data.
 */
const currentData = computed(() => {
  return props.data[currentIndex.value];
});

const onShow = async (): Promise<void> => {
  if (!props.selectedDatum) return;

  currentIndex.value = props.data
    .map((datum) => datum._id)
    .indexOf(props.selectedDatum?._id ?? '');

  await getBorrowingLogData();
};

/**
 * Get the borrowing log data from the server.
 */
const getBorrowingLogData = async (): Promise<void> => {
  try {
    setLoading(true);

    const { data } = await BorrowingServices.getBorrowingLog(
      currentData?.value._id ?? '',
    );

    transactionLog.value = data.data;

    timelineKey.value++;
  } catch (error) {
    console.error(error);
    toast.add({
      error,
      message: 'Error, failed to fetch borrowing log.',
      severity: 'error',
    });
    visible.value = false;
  } finally {
    setLoading(false);
  }
};

watch(currentIndex, async () => {
  await getBorrowingLogData();
});
</script>
<template>
  <TSDialog
    v-if="selectedDatum"
    v-model:visible="visible"
    :draggable="false"
    @show="onShow"
    class="w-[600px]"
    closable
    data-ts-name="borrowing-log"
    modal
  >
    <template #header v-if="transactionLog">
      <div
        class="flex flex-row justify-between items-center w-full"
        data-ts-section="borrowing-log-header"
      >
        <div class="flex items-center gap-2">
          <span
            class="text-[14px] font-bold leading-[normal] tracking-[0.28px]"
          >
            Borrowing Log
          </span>
          <div class="flex items-center gap-1">
            <span class="text-xs font-medium" data-ts-section="transaction-id">
              {{ transactionLog.request.transactionId }}
            </span>
            <CopyButton :value="transactionLog.request.transactionId" />
          </div>
        </div>
        <div class="flex items-center mr-4 gap-2">
          <button
            :disabled="currentIndex === 0"
            @click="currentIndex--"
            class="p-0 text-xs text-primary-500 disabled:text-general-200"
            data-ts-section="prev-button"
          >
            &lt; Prev
          </button>
          <button
            :disabled="currentIndex === data.length - 1"
            @click="currentIndex++"
            class="p-0 text-xs text-primary-500 disabled:text-general-200"
            data-ts-section="next-button"
          >
            Next &gt;
          </button>
        </div>
      </div>
    </template>
    <template #default v-if="transactionLog">
      <div :key="JSON.stringify(transactionLog)" class="flex flex-col gap-3">
        <TSAssetInfo
          :id="transactionLog.request.asset ?? ''"
          :fields="['brand', 'model', 'group']"
        />
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-2">
            <span>Manager:</span>
            <div class="flex items-center gap-[2px]">
              <TSIcon class="h-4 w-4 text-general-400" icon="user" />
              <span class="text-primary-500">
                {{ transactionLog.request.manager.fullName }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <span>Borrower:</span>
            <div class="flex items-center gap-[2px]">
              <TSIcon class="h-4 w-4 text-general-400" icon="user" />
              <span class="text-primary-500">
                {{ transactionLog.request.borrower.fullName }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <span>Status:</span>
          <TSBadge
            :label="transactionLog.request.status ?? ''"
            :severity="statusSeverity"
            format="nowrap"
          />
        </div>
        <TSTabMenu
          v-model:active-index="activeTabIndex"
          :menu="[
            {
              label: 'Borrowing Log',
            },
          ]"
          type="tab"
        />
        <div class="max-h-80 overflow-y-auto">
          <TSTimeline
            :key="timelineKey"
            v-if="activeTabIndex === 0 && transactionLog"
            :data="
              transactionLog.logs.map((log) => {
                return {
                  title: `${log.action} by`,
                  date: log.updatedAt,
                  username: log.userFullName,
                  body: log.detail
                    ? {
                        detail: {
                          ...log.detail,
                          action: log.action,
                        },
                      }
                    : undefined,
                };
              })
            "
          >
            <template #body="slotProps">
              <div v-if="slotProps.value.borrower" class="flex flex-row gap-1">
                <span class="text-xs font-medium text-general-400">
                  Borrower:
                </span>
                <div class="flex gap-[2px] items-center">
                  <TSIcon class="h-4 w-4 !text-general-400" icon="user" />
                  <span class="text-primary-500">
                    {{ formatUserName(slotProps.value.borrower.fullName) }}
                  </span>
                </div>
              </div>
              <div v-if="slotProps.value.duration" class="flex flex-row gap-1">
                <span class="text-xs font-medium text-general-400">
                  Duration:
                </span>
                <span class="text-xs font-medium text-general-800">
                  {{ slotProps.value.duration }} Day(s)
                </span>
              </div>
              <div
                v-if="
                  slotProps.value.approvedDuration &&
                  slotProps.value.action?.includes('Approved')
                "
                class="flex flex-row gap-1"
              >
                <span class="text-xs font-medium text-general-400">
                  Approved Duration:
                </span>
                <span class="text-xs font-medium text-general-800">
                  {{ slotProps.value.approvedDuration }} Day(s)
                </span>
              </div>
              <div
                v-if="
                  slotProps.value.action?.includes('Approved') ||
                  slotProps.value.action?.includes('Rejected')
                "
                class="flex flex-row gap-1"
              >
                <span class="text-xs font-medium text-general-400">Note: </span>
                <span class="text-xs font-medium text-general-800">
                  {{ slotProps.value.notes ?? '-' }}
                </span>
              </div>
              <div v-if="slotProps.value.dueDate" class="flex flex-row gap-1">
                <span class="text-xs font-medium text-general-400">
                  Due Return Date:
                </span>
                <span class="text-xs font-medium text-general-800">
                  {{ formatDate(slotProps.value.dueDate, false) }}
                </span>
              </div>
              <div
                v-if="
                  slotProps.value.from?.duration && slotProps.value.to?.duration
                "
              >
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-general-400">
                    Duration:
                    <span class="text-xs font-medium text-general-800">
                      {{ slotProps.value.from.duration }}
                      Day(s)
                    </span>
                  </span>
                  <svg
                    class="fill-general-400"
                    height="12"
                    viewBox="0 0 12 12"
                    width="12"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.50033 8.086L9.18233 5.404L9.88933 6.111L6.00033 10L2.11133 6.111L2.81833 5.404L5.50033 8.086V2H6.50033V8.086Z"
                    />
                  </svg>
                  <span class="text-xs font-medium text-general-400">
                    Duration:
                    <span class="text-xs font-medium text-general-800">
                      {{ slotProps.value.to.duration }}
                      Day(s)
                    </span>
                  </span>
                </div>
              </div>
              <div
                v-if="slotProps.value.assetImageSmall"
                class="flex flex-col gap-1"
              >
                <span class="text-xs font-medium text-general-400">
                  Damaged Photo:
                </span>
                <TSImage
                  :preview="getImageURL(slotProps.value.assetImageSmall)"
                  :thumbnail="getImageURL(slotProps.value.assetImageSmall)"
                  class="w-9 h-9 max-w-9 max-h-9"
                />
              </div>
              <div v-else class="hidden" />
            </template>
          </TSTimeline>
        </div>
      </div>
    </template>
  </TSDialog>
</template>
